import { apiRoot } from '../apiInstances';
import {
  DVITemplate, DVITemplateTree, DVITemplateCategory, DVITemplatePoint,
  DVIInspection, DVIInspectionFull, DVIInspectionItem, DVIItemStatus,
  ConvertToLineItemInput, DVIPointType, DVIRequirement, DVICustomItemInput,
  JobTemplateListResponse, ApplyJobTemplateResult,
} from '../../types/dvi';

export const dviTemplateApi = {
  async list(): Promise<DVITemplate[]> {
    const { data } = await apiRoot.get('/dvi-templates');
    return data;
  },
  async getById(id: number): Promise<DVITemplateTree> {
    const { data } = await apiRoot.get(`/dvi-templates/${id}`);
    return data;
  },
  async create(input: {
    name: string;
    categories: Array<{
      name: string; sortOrder: number;
      points: Array<{ name: string; description?: string; sortOrder: number; pointType?: DVIPointType; unit?: string | null; requirement?: DVIRequirement }>;
    }>;
  }): Promise<DVITemplateTree> {
    const { data } = await apiRoot.post('/dvi-templates', input);
    return data;
  },
  async update(id: number, input: { name?: string; isDefault?: boolean }): Promise<DVITemplate> {
    const { data } = await apiRoot.put(`/dvi-templates/${id}`, input);
    return data;
  },
  async archive(id: number): Promise<void> {
    await apiRoot.post(`/dvi-templates/${id}/archive`, {});
  },
  async addCategory(templateId: number, input: {
    name: string; sortOrder: number;
  }): Promise<DVITemplateCategory> {
    const { data } = await apiRoot.post(`/dvi-templates/${templateId}/categories`, input);
    return data;
  },
  async updateCategory(catId: number, input: {
    name?: string; sortOrder?: number;
  }): Promise<DVITemplateCategory> {
    const { data } = await apiRoot.put(`/dvi-templates/categories/${catId}`, input);
    return data;
  },
  async deleteCategory(catId: number): Promise<void> {
    await apiRoot.delete(`/dvi-templates/categories/${catId}`);
  },
  async addPoint(catId: number, input: {
    name: string; description?: string; sortOrder: number; pointType?: DVIPointType; unit?: string | null; requirement?: DVIRequirement;
  }): Promise<DVITemplatePoint> {
    const { data } = await apiRoot.post(`/dvi-templates/categories/${catId}/points`, input);
    return data;
  },
  async updatePoint(pointId: number, input: {
    name?: string; description?: string | null; sortOrder?: number; pointType?: DVIPointType; unit?: string | null; requirement?: DVIRequirement;
  }): Promise<DVITemplatePoint> {
    const { data } = await apiRoot.put(`/dvi-templates/points/${pointId}`, input);
    return data;
  },
  async deletePoint(pointId: number): Promise<void> {
    await apiRoot.delete(`/dvi-templates/points/${pointId}`);
  },
};

export const dviApi = {
  async start(workOrderId: number, templateId?: number): Promise<DVIInspectionFull> {
    const { data } = await apiRoot.post(
      `/work-orders/${workOrderId}/dvi`,
      templateId ? { templateId } : {},
    );
    return data;
  },
  async get(workOrderId: number): Promise<DVIInspectionFull | null> {
    try {
      const { data } = await apiRoot.get(`/work-orders/${workOrderId}/dvi`);
      return data;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },
  async updateItem(inspectionId: number, itemId: number, input: {
    status: Exclude<DVIItemStatus, 'pending'>; notes?: string | null;
    // WP-07: picking a preset can set the value AND drive the condition in one
    // go, so these ride along with the status rather than needing a second PUT.
    reading_value?: string | null;
    unit?: string | null;
    measurement_label?: string | null;
  }): Promise<DVIInspectionItem> {
    const { data } = await apiRoot.put(`/dvi/${inspectionId}/items/${itemId}`, input);
    return data;
  },
  /** Add a custom (ad-hoc) inspection point to an inspection. */
  async addItem(inspectionId: number, input: DVICustomItemInput): Promise<DVIInspectionItem> {
    const { data } = await apiRoot.post(`/dvi/${inspectionId}/items`, input);
    return data;
  },
  async updateSummary(inspectionId: number, notes: string): Promise<void> {
    await apiRoot.put(`/dvi/${inspectionId}/summary`, { notes });
  },
  async complete(inspectionId: number): Promise<DVIInspection> {
    const { data } = await apiRoot.post(`/dvi/${inspectionId}/complete`, {});
    return data;
  },
  async delete(inspectionId: number): Promise<void> {
    await apiRoot.delete(`/dvi/${inspectionId}`);
  },
  async convertToLineItem(
    inspectionId: number, itemId: number, input: ConvertToLineItemInput,
  ): Promise<{ lineItem: { id: number; [k: string]: any }; item: DVIInspectionItem }> {
    const { data } = await apiRoot.post(
      `/dvi/${inspectionId}/items/${itemId}/convert-to-line-item`, input,
    );
    return data;
  },

  /**
   * WP-09 — the job templates this finding can be quoted from.
   *
   * Defaults to the finding's own category; `all` widens it to every template
   * the owner has flagged, and `search` does the same across categories.
   * Neither widens past the owner's flag or this shop — that is enforced on
   * the server, not here.
   */
  async listJobTemplates(
    inspectionId: number, itemId: number, opts?: { all?: boolean; search?: string },
  ): Promise<JobTemplateListResponse> {
    const { data } = await apiRoot.get(
      `/dvi/${inspectionId}/items/${itemId}/job-templates`,
      { params: { ...(opts?.all ? { all: '1' } : {}), ...(opts?.search ? { search: opts.search } : {}) } },
    );
    return data;
  },

  /**
   * WP-09 — quote the finding using a template. Only ids travel: the server
   * prices the line from the template plus the shop's current labor rates and
   * markup presets, so the quote cannot be whatever the client typed.
   */
  async applyJobTemplate(
    inspectionId: number, itemId: number, input: { templateId: number; estimateId: number },
  ): Promise<ApplyJobTemplateResult> {
    const { data } = await apiRoot.post(
      `/dvi/${inspectionId}/items/${itemId}/apply-job-template`, input,
    );
    return data;
  },

  async updateReading(inspectionId: number, itemId: number, reading_value: string | null): Promise<DVIInspectionItem> {
    const { data } = await apiRoot.put(`/dvi/${inspectionId}/items/${itemId}`, { reading_value });
    return data;
  },

  /**
   * WP-07 — save the note / measurement on an item WITHOUT a status.
   * A note is an observation about the item, so it must be recordable on a
   * photo-type point and before the technician has judged the point. Fields
   * left undefined are not sent, so the server leaves those columns alone.
   */
  async updateItemNote(
    inspectionId: number,
    itemId: number,
    input: {
      notes?: string | null;
      reading_value?: string | null;
      unit?: string | null;
      measurement_label?: string | null;
    },
  ): Promise<DVIInspectionItem> {
    const { data } = await apiRoot.put(`/dvi/${inspectionId}/items/${itemId}`, input);
    return data;
  },

  /** GET DVI inspection for an estimate (returns null if none exists yet). */
  async getByEstimate(estimateId: number): Promise<DVIInspectionFull | null> {
    try {
      const { data } = await apiRoot.get(`/estimates/${estimateId}/dvi`);
      return data;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  /** POST to create (or return existing) DVI inspection for an estimate. */
  async startForEstimate(estimateId: number, templateId?: number): Promise<DVIInspectionFull> {
    const { data } = await apiRoot.post(
      `/estimates/${estimateId}/dvi`,
      templateId ? { templateId } : {},
    );
    return data;
  },
};
